import { useSEO } from "@/hooks/useSEO";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Camera, CheckCircle2, KeySquare, Zap } from "lucide-react";
import { motion } from "framer-motion";
import AuthShell from "@/components/AuthShell";

export default function RentalReturn() {
  useSEO({ title: "Rental Return — Drop Off in Minutes", description: "Return your Easy Drive rental in minutes: enter your booking details, snap three photos and share the vehicle's location — everything is submitted straight to your booking.", path: "/rental-return" });
  const [ref, setRef] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [dropPhotos, setDropPhotos] = useState({ vehicle: null, key: null, fuel: null });
  const [dropGps, setDropGps] = useState(null);
  const [dropSubmitting, setDropSubmitting] = useState(false);
  const [dropDone, setDropDone] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const r = searchParams.get("ref");
    const e = searchParams.get("email");
    if (r && e) {
      setRef(r);
      setEmail(e);
      const n = searchParams.get("name") || "";
      if (n) setName(n);
      api.get("/bookings/lookup", { params: { ref: r, email: e, name: n || undefined } })
        .then(({ data }) => {
          setBooking(data);
          localStorage.setItem(`booking_email_${data.id}`, e.trim().toLowerCase());
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lookup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.get("/bookings/lookup", { params: { ref, email, name } });
      setBooking(data);
      localStorage.setItem(`booking_email_${data.id}`, email.trim().toLowerCase());
    } catch (e2) {
      setError(errMsg(e2));
    } finally {
      setLoading(false);
    }
  };

  const captureLocation = () => {
    if (!navigator.geolocation) return toast.error("Location is not available on this device");
    navigator.geolocation.getCurrentPosition(
      (pos) => setDropGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => toast.error("Location permission denied — enable it to return your rental remotely"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const submitReturn = async () => {
    if (!dropPhotos.vehicle || !dropPhotos.key || !dropPhotos.fuel)
      return toast.error("Add all three photos: vehicle condition, key placement, fuel gauge");
    if (!dropGps) return toast.error("Share the vehicle's location to complete your rental return");
    setDropSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("vehicle_photo", dropPhotos.vehicle);
      fd.append("key_photo", dropPhotos.key);
      fd.append("fuel_photo", dropPhotos.fuel);
      fd.append("email", email.trim().toLowerCase());
      fd.append("gps_lat", String(dropGps.lat));
      fd.append("gps_lng", String(dropGps.lng));
      if (dropGps.accuracy != null) fd.append("gps_accuracy", String(dropGps.accuracy));
      await api.post(`/bookings/${booking.id}/return-photos`, fd);
      setDropDone(true);
      setBooking({ ...booking, return_submitted: true });
      toast.success(depositWaived ? "Rental return submitted — you're all set once the team verifies the photos" : "Rental return submitted — your deposit releases once the team verifies the photos");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setDropSubmitting(false);
    }
  };

  const depositWaived = booking ? (Number(booking.deposit_amount || 0) <= 0 || booking.deposit_status === "waived") : false;

  if (!booking) {
    return (
      <AuthShell
        testid="rentalreturn-page"
        logoTestid="rentalreturn-logo"
        eyebrow={<><KeySquare size={16} className="gold-text" /> Rental Return</>}
        title={<>Return your <span className="italic gold-text">rental</span></>}
        sub="Enter your booking details, snap three quick photos and share the vehicle's location — everything is submitted straight to your booking."
      >
        <form onSubmit={lookup} className="mt-10 space-y-7">
          <div>
            <Label className="font-ui text-sm tracking-wide text-muted-foreground">Booking reference (e.g. ED-1A2B3C)</Label>
            <Input data-testid="rentalreturn-ref-input" required value={ref} onChange={(e) => setRef(e.target.value)} className="input-dark mt-2 h-12 text-base rounded-2xl px-4" />
          </div>
          <div>
            <Label className="font-ui text-sm tracking-wide text-muted-foreground">Email used at booking</Label>
            <Input data-testid="rentalreturn-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-dark mt-2 h-12 text-base rounded-2xl px-4" />
          </div>
          <div>
            <Label className="font-ui text-sm tracking-wide text-muted-foreground">Full name on booking</Label>
            <Input data-testid="rentalreturn-name-input" required value={name} onChange={(e) => setName(e.target.value)} className="input-dark mt-2 h-12 text-base rounded-2xl px-4" />
          </div>
          {error && <p data-testid="rentalreturn-error" className="text-base text-destructive font-ui">{error}</p>}
          <Button data-testid="rentalreturn-lookup-button" disabled={loading} className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-12 text-base tracking-wide transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(199,30,46,0.35)]">
            {loading ? "Finding booking…" : "Find My Rental"}
          </Button>
        </form>
      </AuthShell>
    );
  }

  return (
    <div data-testid="rentalreturn-dashboard" className="max-w-2xl mx-auto px-6 pt-36 pb-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-card border border-border rounded-3xl p-8 sm:p-10 shadow-[0_24px_60px_rgba(26,10,12,0.08)]"
      >
        <p className="font-ui text-sm tracking-[0.25em] uppercase text-muted-foreground flex items-center gap-2">
          <KeySquare size={16} className="gold-text" /> Rental Return
        </p>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight">{booking.vehicle_name}</h1>
        <p className="mt-3 text-base text-muted-foreground font-ui">
          {booking.booking_ref} · {fmtDate(booking.start_date)} → {fmtDate(booking.end_date)}
        </p>

        {booking.status === "completed" ? (
          <div data-testid="rentalreturn-already-closed" className="mt-8 border border-emerald-400/40 bg-emerald-400/5 rounded-2xl p-6">
            <p className="font-ui font-semibold text-base text-emerald-600 flex items-center gap-2"><CheckCircle2 size={18} /> Rental already returned</p>
            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">This rental is complete — thank you for driving with Easy Drive. Your final invoice is in your email.</p>
          </div>
        ) : booking.status !== "confirmed" ? (
          <div data-testid="rentalreturn-unpaid" className="mt-8 border border-amber-400/40 bg-amber-400/5 rounded-2xl p-6">
            <p className="font-ui font-semibold text-base text-amber-700">Payment still pending</p>
            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">This booking isn't paid yet — complete payment and check-in before returning the vehicle.</p>
            <Link to={`/check-in?ref=${booking.booking_ref}&email=${encodeURIComponent(email)}`} className="block mt-4">
              <Button data-testid="rentalreturn-goto-checkin-button" variant="outline" className="w-full rounded-full border-primary/40 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground font-ui h-12 text-base">
                Go to Express Check-In
              </Button>
            </Link>
          </div>
        ) : (
          <div data-testid="rentalreturn-form" className="mt-8 border border-primary/30 rounded-2xl p-5">
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2">
              <Camera size={14} className="gold-text" /> Submit your return
            </p>
            <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
              Park, take three fresh photos — vehicle condition, key placement, fuel gauge — and share the vehicle's location. Photos must be new: images from your pickup inspection won't be accepted. Everything is attached to your booking{depositWaived ? " — photos confirmed and you're all set." : " and your $150 deposit releases once our team verifies it."}
            </p>
            {booking.return_submitted || dropDone ? (
              <p data-testid="rentalreturn-submitted" className="mt-3 text-xs font-ui text-emerald-600 flex items-center gap-1.5"><CheckCircle2 size={12} /> {depositWaived ? "Return photos submitted — you're all set after verification" : "Return photos submitted — deposit releases after verification"}</p>
            ) : (
              <div className="mt-4 space-y-3">
                {[{ k: "vehicle", l: "Vehicle condition photo" }, { k: "key", l: "Key placement photo" }, { k: "fuel", l: "Fuel gauge photo" }].map((p) => (
                  <div key={p.k}>
                    <Input data-testid={`rentalreturn-photo-${p.k}`} type="file" accept="image/*" capture="environment" onChange={(e) => setDropPhotos({ ...dropPhotos, [p.k]: e.target.files?.[0] || null })} className="input-dark text-xs file:text-primary file:mr-2" />
                    <p className="mt-1 text-[10px] font-ui text-muted-foreground">
                      {p.l} {dropPhotos[p.k] && <span data-testid={`rentalreturn-photo-done-${p.k}`} className="text-emerald-600 font-semibold">ready ✓</span>}
                    </p>
                  </div>
                ))}
                <Button data-testid="rentalreturn-location-button" type="button" variant="outline" onClick={captureLocation} className="w-full rounded-full border-border bg-transparent hover:bg-black/5 font-ui">
                  {dropGps ? `Location captured ✓ (${dropGps.lat.toFixed(4)}, ${dropGps.lng.toFixed(4)})` : "Share Vehicle Location"}
                </Button>
                <Button data-testid="rentalreturn-submit-button" onClick={submitReturn} disabled={dropSubmitting} className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-12">
                  {dropSubmitting ? "Submitting…" : "Submit Rental Return"}
                </Button>
              </div>
            )}
          </div>
        )}

        <Link to={`/check-in?ref=${booking.booking_ref}&email=${encodeURIComponent(email)}`} className="block mt-4">
          <Button data-testid="rentalreturn-checkin-link" variant="outline" className="w-full rounded-full border-border bg-transparent hover:bg-black/5 hover:text-primary font-ui h-12 text-base">
            <Zap size={16} className="mr-2" /> Picking up instead? Express Check-In
          </Button>
        </Link>
      </motion.div>
    </div>
  );
}
